import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiMapPin, FiDollarSign, FiArrowRight } from 'react-icons/fi';
import Navbar from '../../components/navbar/navbar';
import UserNav from '../../components/navbar/user-nav';
import Footer from '../../components/footer';
import SEO from '../../components/SEO';
import { useUser } from '../../context/UserContext';
import { ZONAS_DATA, Zona } from '../../data/zonas-data';

export default function Zonas() {
  const { t } = useTranslation();
  const { user } = useUser();

  const zonasSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Zonas de Inversión Inmobiliaria en Riviera Nayarit',
    description: 'Explora las mejores zonas para invertir en bienes raíces en Riviera Nayarit',
    url: 'https://ampirivieranayarit.com/modulob/zonas',
    numberOfItems: ZONAS_DATA.length,
    itemListElement: ZONAS_DATA.map((zona, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: zona.nombre,
      url: `https://ampirivieranayarit.com/modulob/zonas/${zona.slug}`
    }))
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  return (
    <>
      <SEO
        title="Zonas de Inversión Inmobiliaria"
        description="Explora las mejores zonas para invertir en Riviera Nayarit: Punta de Mita, Sayulita, Bucerías, Nuevo Vallarta, La Cruz y más. Propiedades con asesoría AMPI certificada."
        path="/modulob/zonas"
        keywords="zonas riviera nayarit, invertir punta de mita, propiedades sayulita, bienes raíces bucerías, nuevo vallarta inmuebles, bahía de banderas inversión"
        schema={zonasSchema}
      />

      {user ? (
        <UserNav
          nombreUsuario={user.nombre}
          nombreInmobiliaria={user.inmobiliaria_nombre}
        />
      ) : (
        <Navbar transparent={false} />
      )}

      {/* Hero Section */}
      <section className="py-5" style={{ backgroundColor: '#0a2f27' }}>
        <div className="container">
          <div className="row justify-content-center text-center">
            <div className="col-lg-8">
              <h1 className="text-white fw-bold mb-3">
                Zonas de Inversión en Riviera Nayarit
              </h1>
              <p className="text-white-50 fs-5 mb-4">
                Descubre las mejores ubicaciones para invertir en bienes raíces.
                Desde el lujo exclusivo de Punta de Mita hasta el encanto bohemio de Sayulita.
              </p>
              <div className="d-flex justify-content-center gap-3 flex-wrap">
                <span className="badge bg-light text-dark px-3 py-2">
                  <FiMapPin className="me-2" />
                  {ZONAS_DATA.length} Zonas Destacadas
                </span>
                <span className="badge bg-light text-dark px-3 py-2">
                  <FiDollarSign className="me-2" />
                  Desde $100,000 USD
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Zonas Grid */}
      <section className="py-5">
        <div className="container">
          <div className="row g-4">
            {ZONAS_DATA.map((zona: Zona) => (
              <div className="col-lg-6" key={zona.id}>
                <div
                  className="card border-0 shadow-sm h-100"
                  style={{ borderRadius: '12px', overflow: 'hidden' }}
                >
                  {/* Header con gradiente */}
                  <div
                    className="px-4 py-4"
                    style={{
                      backgroundColor: '#0a2f27',
                      backgroundImage: 'linear-gradient(135deg, #0a2f27 0%, #1a4a3f 100%)'
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <h3 className="text-white fw-bold mb-2">{zona.nombre}</h3>
                        <p className="text-white-50 mb-0 small">
                          <FiMapPin className="me-1" />
                          {zona.ubicacion}
                        </p>
                      </div>
                      <span
                        className="badge px-3 py-2"
                        style={{ backgroundColor: '#02de7f', color: '#0a2f27' }}
                      >
                        Desde {formatPrice(zona.rangoPreciosUSD.min)}
                      </span>
                    </div>
                  </div>

                  {/* Body */}
                  <div className="card-body p-4">
                    <p className="text-muted mb-4">
                      {zona.descripcion}
                    </p>

                    {/* Características principales */}
                    <div className="mb-4">
                      <h6 className="fw-bold mb-2" style={{ color: '#0a2f27' }}>
                        Características
                      </h6>
                      <div className="d-flex flex-wrap gap-2">
                        {zona.caracteristicas.slice(0, 3).map((car, idx) => (
                          <span
                            key={idx}
                            className="badge bg-light text-dark px-2 py-1"
                          >
                            {car}
                          </span>
                        ))}
                        {zona.caracteristicas.length > 3 && (
                          <span className="badge bg-light text-muted px-2 py-1">
                            +{zona.caracteristicas.length - 3} más
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Puntos clave */}
                    <div className="row g-2 mb-4">
                      {zona.puntosClave.slice(0, 2).map((punto, idx) => (
                        <div className="col-6" key={idx}>
                          <div
                            className="p-2 rounded"
                            style={{ backgroundColor: 'rgba(2, 222, 127, 0.1)' }}
                          >
                            <small className="fw-bold d-block" style={{ color: '#0a2f27' }}>
                              {punto.titulo}
                            </small>
                            <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                              {punto.descripcion.substring(0, 50)}...
                            </small>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Distancias */}
                    <div className="d-flex justify-content-between text-muted small mb-4">
                      <span>Aeropuerto: {zona.distancias.aeropuerto}</span>
                      <span>Playa: {zona.distancias.playa}</span>
                    </div>

                    {/* Botones */}
                    <div className="d-flex gap-2">
                      <Link
                        to={`/zonas/${zona.slug}`}
                        className="btn btn-primary flex-grow-1"
                      >
                        Ver Zona
                        <FiArrowRight className="ms-2" />
                      </Link>
                      <Link
                        to={`/grid?zona=${zona.slug}`}
                        className="btn btn-outline-primary"
                      >
                        Propiedades
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mapa Section */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <h2 className="fw-bold mb-3" style={{ color: '#0a2f27' }}>
                Ubicación Privilegiada
              </h2>
              <p className="text-muted mb-4">
                Riviera Nayarit se extiende a lo largo de 307 km de costa en el Pacífico mexicano,
                desde Nuevo Vallarta hasta San Blas. Con acceso directo desde el Aeropuerto
                Internacional de Puerto Vallarta, es uno de los destinos más accesibles de México
                para inversores internacionales.
              </p>
              <ul className="list-unstyled">
                <li className="mb-2">
                  <span className="badge bg-primary me-2">PVR</span>
                  Aeropuerto Internacional a 10-45 min de cualquier zona
                </li>
                <li className="mb-2">
                  <span className="badge bg-success me-2">USA</span>
                  Vuelos directos desde 30+ ciudades de EE.UU. y Canadá
                </li>
                <li className="mb-2">
                  <span className="badge bg-info me-2">MX</span>
                  Conexiones diarias desde Ciudad de México, Guadalajara y Monterrey
                </li>
              </ul>
              <Link to="/contact" className="btn btn-primary mt-3">
                Hablar con un Asesor
              </Link>
            </div>
            <div className="col-lg-6">
              <div
                className="rounded-3 overflow-hidden shadow"
                style={{ height: '400px', backgroundColor: '#e9ecef' }}
              >
                {/* Placeholder para mapa - se puede integrar Google Maps */}
                <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                  <div className="text-center">
                    <FiMapPin size={48} className="mb-3" />
                    <p>Mapa Interactivo de Riviera Nayarit</p>
                    <small>Coordenadas: 20.75° N, 105.40° W</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-5" style={{ backgroundColor: '#0a2f27' }}>
        <div className="container text-center">
          <h2 className="text-white fw-bold mb-3">
            ¿Listo para invertir en Riviera Nayarit?
          </h2>
          <p className="text-white-50 mb-4">
            Nuestros asesores certificados AMPI te guían en cada paso del proceso
          </p>
          <div className="d-flex justify-content-center gap-3 flex-wrap">
            <Link
              to="/grid"
              className="btn btn-lg px-5"
              style={{ backgroundColor: '#02de7f', color: '#0a2f27', fontWeight: 600 }}
            >
              Explorar Propiedades
            </Link>
            <Link
              to="/agents"
              className="btn btn-lg btn-outline-light px-5"
            >
              Encontrar Asesor
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
